import { SharePointListClient, type ListItem } from '../SharePointListClient';

/**
 * 99.ABM_ParametrosComDesc — commission / discount parameters. Edit-only in
 * Configuración: admins tune values, never create or delete rows.
 *
 * ---
 * Internal-name quirk: `ValorPorcentaje_PCD` (internal) ↔ `ValorAplicado_PCD`
 * (display). Graph API returns the INTERNAL name on reads and accepts only
 * the internal name on writes. We expose a single `ValorPorcentaje_PCD` field
 * on the typed payload; the UI label remains "Valor aplicado".
 *
 * Values are stored as text with Spanish comma-decimal (e.g. `"0,1"` for 10%,
 * `"13000000"` for a Monto). When the unit (`TipoValor_PCD` /
 * `TipoValorAplicado_PCD`) is 'Porcentaje', the stored value is the fraction
 * (0.1 for 10%). Non-percentage units store the value as-is.
 */
export type ParametroComDescFields = {
  Title?: string | null;
  Parametro_PCD?: string | null;
  Tipo_PCD?: string | null;
  Valor_PCD?: string | number | null;
  /**
   * Internal name. Maps to display `ValorAplicado_PCD`. Semantically "the
   * second value" — a percent rate, a count, or a monto depending on
   * `TipoValorAplicado_PCD`.
   */
  ValorPorcentaje_PCD?: string | number | null;
  TipoValor_PCD?: 'Porcentaje' | 'Cantidad' | 'Monto' | string | null;
  TipoValorAplicado_PCD?: 'Porcentaje' | 'Cantidad' | 'Monto' | string | null;
  Status_PCD?: string | null;
};

export type ParametroComDescItem = ListItem<ParametroComDescFields>;

export const parametrosComDescClient = new SharePointListClient<ParametroComDescFields>(
  'ABM_ParametrosComDesc',
);

/** Parametros list has no status column; we always list everything. */
export async function listAllParametrosComDesc(): Promise<ParametroComDescItem[]> {
  return parametrosComDescClient.listItems({
    orderBy: 'fields/Parametro_PCD asc',
    top: 5000,
  });
}

/** The admin only ever edits the two value fields. */
export type ParametroComDescPayload = {
  /** Raw value as the admin typed it (percentage as "10", not 0.10). */
  valor: number;
  /** Raw value as the admin typed it. */
  valorAplicado: number;
};

/**
 * Parse a stored value (string with Spanish comma-decimal OR number) into
 * a JS number. Returns 0 on anything unparseable.
 */
export function parseStoredValue(raw: string | number | null | undefined): number {
  if (raw == null) return 0;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  // Strip thousand-dots first, then swap decimal comma for dot.
  const normalized = trimmed.replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

/** Format a JS number as a Spanish comma-decimal string (no thousand separators). */
function toStoredString(n: number): string {
  if (!Number.isFinite(n)) return '0';
  // Keep up to 6 decimals to preserve precision on fractions like 0.025; trim
  // trailing zeros so "0.1" round-trips as "0,1".
  const fixed = n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  return fixed.replace('.', ',');
}

function normalise(raw: number, tipo: string | null | undefined): number {
  return tipo === 'Porcentaje' ? raw / 100 : raw;
}

export async function updateParametroComDesc(
  item: ParametroComDescItem,
  payload: ParametroComDescPayload,
): Promise<ParametroComDescItem> {
  const nextValor = normalise(payload.valor, item.fields.TipoValor_PCD);
  const nextValorAplicado = normalise(
    payload.valorAplicado,
    item.fields.TipoValorAplicado_PCD,
  );
  return parametrosComDescClient.updateItem(item.id, {
    Valor_PCD: toStoredString(nextValor),
    // Internal name — Graph rejects the display form 'ValorAplicado_PCD'.
    ValorPorcentaje_PCD: toStoredString(nextValorAplicado),
  });
}
